import React, { lazy } from 'react';

import { PageConfig } from '@sms/plasma-ui';
import { useJournalConfig } from '@sms/journal-commons-journal-commons-ui';

import {
  BuildNewProcessPage,
  DeployProcessPage,
  DocumentationProcessPage,
  EditProcessPage,
  InstanceProcessPage,
  MonitorProcessPage,
  ProcessesPage,
  StartProcessPage,
} from '@sms/fluere-process-fluere-process-ui';

import {
  CreateReportPage,
  CreateTemplatePage,
  EditTemplatePage,
  MainPage,
} from '@sms/report-report-ui';

import { EmbeddedPage } from './EmbeddedPage';
import { PageWithMenu } from './PageWithMenu';
import { PageWithBookEditMenu } from './PageWithBookEditMenu';
import { TestEmbeddedPage } from './TestEmbeddedPage';
import { TestTaskEmbeddedPage } from './TestTaskEmbeddedPage';
import app from '../config/app.json';
import RootPage from './RootPage';
import BookSelectedPage from './BookSelectedPage/BookSelectedPage';

const DashboardListPage = lazy(() => import('@sms/dashboard-dashboard-ui').then(module => ({ default: module.DashboardListPage })));
const DashboardViewPage = lazy(() => import('@sms/dashboard-dashboard-ui').then(module => ({ default: module.DashboardViewPage })));

const DecisionTableListPage = lazy(() => import('@sms/decision-table-decision-ui').then(module => ({ default: module.DecisionTableListPage })));
const DecisionTableEditPage = lazy(() => import('@sms/decision-table-decision-ui').then(module => ({ default: module.DecisionTableEditPage })));
const DecisionTableCreatePage = lazy(() => import('@sms/decision-table-decision-ui').then(module => ({ default: module.DecisionTableCreatePage })));

const CreateRulePage = lazy(() => import('@sms/journal-cep-journal-cep-ui').then(module => ({ default: module.CreateRulePage })));
const EditRulePage = lazy(() => import('@sms/journal-cep-journal-cep-ui').then(module => ({ default: module.EditRulePage })));
const RuleListPage = lazy(() => import('@sms/journal-cep-journal-cep-ui').then(module => ({ default: module.RuleListPage })));

const CreateEventPage = lazy(() => import('@sms/journal-event-journal-event-ui').then(module => ({ default: module.CreateEventPage })));
const EditEventPage = lazy(() => import('@sms/journal-event-journal-event-ui').then(module => ({ default: module.EditEventPage })));
const EventListPage = lazy(() => import('@sms/journal-event-journal-event-ui').then(module => ({ default: module.EventListPage })));
const EventViewPage = lazy(() => import('@sms/journal-event-journal-event-ui').then(module => ({ default: module.EventViewPage })));
const CreateEventTemplatePageWithProvider = lazy(() => import('@sms/journal-event-journal-event-ui').then(module => ({ default: module.CreateEventTemplatePageWithProvider })));
const EditEventTemplatePageWithProvider = lazy(() => import('@sms/journal-event-journal-event-ui').then(module => ({ default: module.EditEventTemplatePageWithProvider })));

const TaskPage = lazy(() => import('@sms/journal-task-journal-task-ui').then(module => ({ default: module.TaskPage })));
const TaskListPage = lazy(() => import('@sms/journal-task-journal-task-ui').then(module => ({ default: module.TaskListPage })));
const CreateTaskPage = lazy(() => import('@sms/journal-task-journal-task-ui').then(module => ({ default: module.CreateTaskPage })));
const EditTaskPage = lazy(() => import('@sms/journal-task-journal-task-ui').then(module => ({ default: module.EditTaskPage })));
const CreateTaskTemplatePage = lazy(() => import('@sms/journal-task-journal-task-ui').then(module => ({ default: module.CreateTemplatePage })));
const EditTaskTemplatePage = lazy(() => import('@sms/journal-task-journal-task-ui').then(module => ({ default: module.EditTemplatePage })));

const BooksPage = lazy(() => import('@sms/journal-books-journal-books-ui').then(module => ({ default: module.BooksPage })));
const SettingsPage = lazy(() => import('@sms/journal-books-journal-books-ui').then(module => ({ default: module.SettingsPage })));
const CreateBookPage = lazy(() => import('@sms/journal-books-journal-books-ui').then(module => ({ default: module.CreateBookPage })));
const EditBookPage = lazy(() => import('@sms/journal-books-journal-books-ui').then(module => ({ default: module.EditBookPage })));

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
const SUB_PATH = (window as any).plasma?.environment?.SUB_PATH ?? '/';
const PROCESSED_SUB_PATH = SUB_PATH.replace(/\/$/, '');

const withMenu = (PageComponent: React.ComponentType<any>, isBooksMenu?: boolean) => {
  return (props: any) => {
    const { hideMenu } = useJournalConfig({ persistInSessionStore: true });

    if (hideMenu)
      return (
        <EmbeddedPage>
          <PageComponent {...props} />
        </EmbeddedPage>
      );

    return (
      <PageWithMenu isBooksMenu={isBooksMenu}>
        <PageComponent {...props} />
      </PageWithMenu>
    );
  };
};

const withBookEditMenu = (PageComponent: React.ComponentType<any>) => {
  return (props: any) => {
    const { hideMenu } = useJournalConfig({ persistInSessionStore: true });

    if (hideMenu)
      return (
        <EmbeddedPage>
          <PageComponent {...props} />
        </EmbeddedPage>
      );

    return (
      <PageWithBookEditMenu>
        <PageComponent {...props} />
      </PageWithBookEditMenu>
    );
  };
};

function prefixPath(path: string) {
  const treatedPath = path.replace(/^\//, '');
  return `${PROCESSED_SUB_PATH}/${treatedPath}`.replace(/\/\//g, '/');
}

const pages: PageConfig[] = [
  {
    key: 'Root',
    path: '/',
    page: RootPage,
  },
  {
    key: 'BooksMenu',
    path: prefixPath(`/${app.code}/book/selected/:bookId`),
    page: BookSelectedPage,
  },
  {
    key: 'RootAppCode',
    path: prefixPath(`/${app.code}`),
    page: RootPage,
  },
  {
    key: 'Processes',
    path: prefixPath(`/${app.code}/book/:bookId/processes`),
    page: withMenu(ProcessesPage),
    title: 'app.menu.processes',
    permissions: ['process.view'],
  },
  {
    key: 'NewProcesses',
    path: prefixPath(`/${app.code}/book/:bookId/processes/new`),
    page: withMenu(BuildNewProcessPage),
    title: 'app.menu.newProcess',
    permissions: ['process.definition.edit'],
  },
  {
    key: 'DeployProcesses',
    path: prefixPath(`/${app.code}/book/:bookId/processes/deploy/:modelId`),
    page: withMenu(DeployProcessPage),
    title: 'app.menu.deployProcess',
    permissions: ['process.definition.edit'],
  },
  {
    key: 'StartProcess',
    path: prefixPath(`/${app.code}/book/:bookId/processes/start/:id`),
    page: withMenu(StartProcessPage),
    title: 'app.menu.startProcess',
    permissions: ['process.edit'],
  },
  {
    key: 'DocumentationProcessPage',
    path: prefixPath(`/${app.code}/book/:bookId/processes/documentation/:id`),
    page: withMenu(DocumentationProcessPage),
    title: 'app.menu.documentationProcess',
    permissions: ['process.view'],
  },
  {
    key: 'MonitorProcessPage',
    path: prefixPath(`/${app.code}/book/:bookId/processes/monitor/:id`),
    page: withMenu(MonitorProcessPage),
    title: 'app.menu.monitorProcess',
    permissions: ['process.view'],
  },
  {
    key: 'InstanceProcessPage',
    path: prefixPath(`/${app.code}/book/:bookId/processes/monitor/:id/instance/:instanceId`),
    page: withMenu(InstanceProcessPage),
    title: 'app.menu.instanceProcess',
    permissions: ['process.view'],
  },
  {
    key: 'EditProcessPage',
    path: prefixPath(`/${app.code}/book/:bookId/processes/edit/:id`),
    page: withMenu(EditProcessPage),
    title: 'app.menu.editProcess',
    permissions: ['process.definition.edit'],
  },
  {
    key: 'TaskList',
    path: prefixPath(`/${app.code}/book/:bookId/tasks`),
    page: withMenu(TaskListPage),
    title: 'app.menu.tasks',
    permissions: ['task.view'],
  },
  {
    key: 'CreateTaskTemplate',
    path: prefixPath(`/${app.code}/settings/tasks/templates/new`),
    page: withMenu(CreateTaskTemplatePage, true),
    permissions: ['task.template.edit'],
  },
  {
    key: 'EditTaskTemplate',
    path: prefixPath(`/${app.code}/settings/tasks/templates/edit/:id`),
    page: withMenu(EditTaskTemplatePage, true),
    permissions: [['task.template.view']],
  },
  {
    key: 'Task',
    path: prefixPath(`/${app.code}/book/:bookId/tasks/execute/:taskId`),
    page: withMenu(TaskPage),
    permissions: ['task.view'],
  },
  {
    key: 'CreateTask',
    path: prefixPath(`/${app.code}/book/:bookId/tasks/create`),
    page: withMenu(CreateTaskPage),
    permissions: ['task.edit'],
  },
  {
    key: 'EditTask',
    path: prefixPath(`/${app.code}/book/:bookId/tasks/edit/:taskId`),
    page: withMenu(EditTaskPage),
    permissions: ['task.edit'],
  },
  // TODO: remover esse endpoint de teste e a página associada
  {
    key: 'TestTaskEmbeddedPage',
    path: prefixPath(`/${app.code}/book/:bookId/tasks/test-embedded`),
    page: TestTaskEmbeddedPage,
    permissions: ['task.view'],
  },
  {
    key: 'Report',
    path: prefixPath(`/${app.code}/book/:bookId/reports`),
    page: withMenu(MainPage),
    title: 'app.menu.reports',
    permissions: ['report.view'],
  },
  {
    key: 'NewReport',
    path: prefixPath(`/${app.code}/book/:bookId/reports/new`),
    page: withMenu(CreateReportPage),
    title: 'app.menu.reports',
    permissions: ['report.edit'],
  },
  {
    key: 'NewReportTemplate',
    path: prefixPath(`/${app.code}/settings/reports/templates/new`),
    page: withMenu(CreateTemplatePage, true),
    title: 'app.menu.reports',
    permissions: ['report.template.edit'],
  },
  {
    key: 'EditReportTemplate',
    path: prefixPath(`/${app.code}/settings/reports/templates/edit/:id`),
    page: withMenu(EditTemplatePage, true),
    title: 'app.menu.reports',
    permissions: ['report.template.view'],
  },
  {
    key: 'EventList',
    path: prefixPath(`/${app.code}/book/:bookId/events`),
    page: withMenu(EventListPage),
    permissions: ['event.view'],
  },
  // TODO: remover esse endpoint de teste e a página associada
  {
    key: 'TestEmbeddedPage',
    path: prefixPath(`/${app.code}/book/:bookId/events/test-embedded`),
    page: TestEmbeddedPage,
    permissions: ['event.view'],
  },
  {
    key: 'CreateEventTemplates',
    path: prefixPath(`/${app.code}/settings/events/templates/create`),
    page: withMenu(CreateEventTemplatePageWithProvider, true),
    permissions: ['event.template.edit'],
  },
  {
    key: 'EditEventTemplates',
    path: prefixPath(`/${app.code}/settings/events/templates/edit/:id`),
    page: withMenu(EditEventTemplatePageWithProvider, true),
    permissions: ['event.template.view'],
  },
  {
    key: 'EventCreate',
    path: prefixPath(`/${app.code}/book/:bookId/events/create`),
    page: withMenu(CreateEventPage),
    permissions: ['event.edit'],
  },
  {
    key: 'EventEdit',
    path: prefixPath(`/${app.code}/book/:bookId/events/:id/edit`),
    page: withMenu(EditEventPage),
    permissions: ['event.edit'],
  },
  {
    key: 'EventView',
    path: prefixPath(`/${app.code}/book/:bookId/events/:id`),
    page: withMenu(EventViewPage),
    permissions: ['event.view'],
  },
  {
    key: 'RuleList',
    path: prefixPath(`/${app.code}/book/:bookId/rules`),
    page: withMenu(RuleListPage),
    permissions: ['rule.view'],
  },
  {
    key: 'CreateRule',
    path: prefixPath(`/${app.code}/book/:bookId/rules/create`),
    page: withMenu(CreateRulePage),
    permissions: ['rule.edit'],
  },
  {
    key: 'EditRule',
    path: prefixPath(`/${app.code}/book/:bookId/rules/edit/:id`),
    page: withMenu(EditRulePage),
    permissions: ['rule.edit'],
  },
  {
    key: 'ViewRule',
    path: prefixPath(`/${app.code}/book/:bookId/rules/view/:id`),
    page: withMenu(EditRulePage),
    permissions: ['rule.view'],
  },
  {
    key: 'Decision tables list',
    path: prefixPath(`/${app.code}/book/:bookId/decisionTables`),
    page: withMenu(DecisionTableListPage),
    permissions: ['decision.view'],
  },
  {
    key: 'Edit decision table',
    path: prefixPath(`/${app.code}/book/:bookId/decisionTables/edit/:id`),
    page: withMenu(DecisionTableEditPage),
    permissions: ['decision.view'],
  },
  {
    key: 'Create decision table',
    path: prefixPath(`/${app.code}/book/:bookId/decisionTables/create`),
    page: withMenu(DecisionTableCreatePage),
    permissions: ['decision.edit'],
  },
  {
    key: 'BoardList',
    path: prefixPath(`/${app.code}/book/:bookId/boards`),
    page: withMenu(DashboardListPage),
    permissions: ['dashboard.view'],
  },
  {
    key: 'BoardRule',
    path: prefixPath(`/${app.code}/book/:bookId/boards/view/:id`),
    page: withMenu(DashboardViewPage),
    permissions: ['dashboard.view'],
  },
  {
    key: 'BooksPage',
    path: prefixPath(`/${app.code}/books`),
    page: withMenu(BooksPage, true),
    permissions: ['books.view'],
  },
  {
    key: 'SettingsPage',
    path: prefixPath(`/${app.code}/settings`),
    page: withMenu(SettingsPage, true),
    permissions: ['books.settings.view'],
  },
  {
    key: 'CreateBookPage',
    path: prefixPath(`/${app.code}/books/create`),
    page: withMenu(CreateBookPage, true),
    permissions: ['books.view'],
  },
  {
    key: 'EditBookPage',
    path: prefixPath(`/${app.code}/book/edit/:bookId`),
    page: withBookEditMenu(EditBookPage),
    permissions: ['books.view'],
  }
];

export default pages;
